/**
 * SectionEditorPanel — the per-section editor screen. It reads the section's supported
 * layouts and content capabilities from the SAME builtin registry the preview renders
 * with (never hardcoded), then renders only the controls that section kind supports:
 * a layout selector, heading-group text, an items/nav-links editor, an actions editor,
 * and contact/location fields. Every control edits the live working schema.
 */
import { Plus, Trash2 } from 'lucide-react'
import type { LayoutId } from '../../site-builder/schema'
import { builderRegistry } from '../previewRuntime'
import { useBuilderEditor } from './editorContext'
import { Fieldset, SegmentedControl, TextField, TextArea } from './controls'

function labelize(v: string): string {
  return v.charAt(0).toUpperCase() + v.slice(1)
}

export function SectionEditorPanel({ sectionId }: { sectionId: string }) {
  const e = useBuilderEditor()
  const section = e.schema.pages[0]?.sections.find((s) => s.id === sectionId)
  if (!section) {
    return <p className="px-5 py-6 text-xs text-gray-400">This section is no longer available.</p>
  }

  const entry = builderRegistry.entry(section.kind)
  const caps = entry?.contentCapabilities
  const layouts = (entry?.supportedLayouts ?? []) as readonly LayoutId[]
  const content = section.content
  const isNavList = section.kind === 'navbar' || section.kind === 'footer'
  const items = content.items ?? []
  const actions = content.actions ?? []

  return (
    <div>
      <Fieldset title="Layout">
        {layouts.length > 1 ? (
          <SegmentedControl<LayoutId>
            label="Layout"
            value={section.layout}
            options={layouts.map((l) => ({ value: l, label: labelize(l) }))}
            onChange={(v) => e.setSectionLayout(sectionId, v)}
          />
        ) : (
          <p className="text-[11px] text-gray-400">This section has a single layout.</p>
        )}
      </Fieldset>

      {caps?.eyebrow || caps?.heading || caps?.subheading ? (
        <Fieldset title="Content">
          {caps?.eyebrow ? (
            <TextField label="Eyebrow" value={content.eyebrow ?? ''} onChange={(v) => e.setSectionText(sectionId, 'eyebrow', v)} />
          ) : null}
          {caps?.heading ? (
            <TextField label="Heading" value={content.heading ?? ''} onChange={(v) => e.setSectionText(sectionId, 'heading', v)} />
          ) : null}
          {caps?.subheading ? (
            <TextArea label="Subheading" value={content.subheading ?? ''} rows={3} onChange={(v) => e.setSectionText(sectionId, 'subheading', v)} />
          ) : null}
        </Fieldset>
      ) : null}

      {caps?.items ? (
        <Fieldset title={isNavList ? 'Links' : 'Items'}>
          {items.map((it) => (
            <div key={it.id} className="flex flex-col gap-2 rounded-xl border border-gray-100 p-3">
              {isNavList ? (
                <>
                  <TextField label="Label" value={it.title} onChange={(v) => e.setNavLink(sectionId, it.id, 'title', v)} />
                  <TextField label="Link" value={it.value ?? ''} onChange={(v) => e.setNavLink(sectionId, it.id, 'value', v)} />
                </>
              ) : (
                <>
                  <TextField label="Title" value={it.title} onChange={(v) => e.setItemField(sectionId, it.id, 'title', v)} />
                  <TextArea label="Description" value={it.description ?? ''} rows={2} onChange={(v) => e.setItemField(sectionId, it.id, 'description', v)} />
                  {section.kind === 'reviews' ? (
                    <TextField label="Rating (0–5)" value={String(it.rating ?? '')} onChange={(v) => e.setItemRating(sectionId, it.id, Math.max(0, Math.min(5, Number(v) || 0)))} />
                  ) : null}
                </>
              )}
              <button
                type="button"
                onClick={() => e.removeItem(sectionId, it.id)}
                className="flex items-center gap-1 self-start rounded-lg px-2 py-1 text-xs font-medium text-gray-500 transition-colors hover:text-red-600"
              >
                <Trash2 className="h-3.5 w-3.5" /> Remove
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => e.addItem(sectionId)}
            className="flex items-center justify-center gap-1 rounded-lg border border-dashed border-gray-300 px-3 py-2 text-xs font-semibold text-gray-600 transition-colors hover:bg-gray-50"
          >
            <Plus className="h-3.5 w-3.5" /> Add {isNavList ? 'link' : 'item'}
          </button>
        </Fieldset>
      ) : null}

      {caps?.actions && actions.length > 0 ? (
        <Fieldset title="Buttons">
          {actions.map((a) => (
            <div key={a.id} className="flex flex-col gap-2 rounded-xl border border-gray-100 p-3">
              <TextField label="Label" value={a.label} onChange={(v) => e.setActionField(sectionId, a.id, 'label', v)} />
              <TextField label="Link / value" value={a.value ?? ''} onChange={(v) => e.setActionField(sectionId, a.id, 'value', v)} />
            </div>
          ))}
        </Fieldset>
      ) : null}

      {caps?.contact ? (
        <Fieldset title="Contact details">
          <TextField label="Phone" value={content.contact?.phone ?? ''} onChange={(v) => e.setContactField(sectionId, 'phone', v)} />
          <TextField label="Email" value={content.contact?.email ?? ''} onChange={(v) => e.setContactField(sectionId, 'email', v)} />
          <TextField label="Website" value={content.contact?.website ?? ''} onChange={(v) => e.setContactField(sectionId, 'website', v)} />
        </Fieldset>
      ) : null}

      {caps?.location ? (
        <Fieldset title="Location">
          <TextField label="Address" value={content.location?.address ?? ''} onChange={(v) => e.setLocationField(sectionId, 'address', v)} />
        </Fieldset>
      ) : null}
    </div>
  )
}
